import { AuthService } from '@App/app/auth/auth.service';
import { Credits } from '@App/app/entities/admin-panel/credits.model';
import { selectAllUserCompanies } from '@App/app/shared/store/user-companies/selectors/user-companies.selectors';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { selectIsResolutionDataProcessed } from '../../store/files-resolution/files-resolution.selectors';
import { selectFiles } from '../../store/files/selectors/files.selectors';

/**
 * Pure builders for the derived observable streams used by
 * {@link UploaderStorageService}. Extracted to keep the service lean.
 */

// TODO: extract reusable selector for getting credits
export const buildCredits$ = (store: Store, authService: AuthService): Observable<Credits | null> =>
  combineLatest([authService.currentEndpoint$, store.select(selectAllUserCompanies)]).pipe(
    map(([endpoint, companies]) => {
      if (endpoint && companies) {
        return companies.find((company) => company.id === endpoint.id)?.credits || null;
      }
      return null;
    }),
  );

export const buildIsEnoughCredits$ = (
  totalPixelsAmount$: Observable<number | null>,
  credits$: Observable<Credits | null>,
): Observable<boolean | null> =>
  combineLatest([totalPixelsAmount$, credits$]).pipe(
    map(([totalPixelAmount, credits]) =>
      totalPixelAmount === null || credits === null ? null : totalPixelAmount <= credits.value,
    ),
  );

export const buildCreditsStartUploadCondition$ = (
  store: Store,
  isCreditsEnabled$: Observable<boolean | null>,
  isEnoughCredits$: Observable<boolean | null>,
): Observable<boolean> =>
  combineLatest([
    isCreditsEnabled$,
    isEnoughCredits$,
    store.select(selectIsResolutionDataProcessed),
  ]).pipe(
    map(
      ([isCreditsEnabled, isEnoughCredits, isResolutionDataProcessed]) =>
        isCreditsEnabled === false ||
        (isCreditsEnabled === true && isEnoughCredits === true && isResolutionDataProcessed),
    ),
  );

export const buildCanStartUpload$ = (
  store: Store,
  uploadStarted$: Observable<boolean>,
  isPendingUpload$: Observable<boolean>,
  creditsStartUploadCondition$: Observable<boolean>,
): Observable<boolean> =>
  combineLatest([
    uploadStarted$,
    isPendingUpload$,
    store.select(selectFiles),
    creditsStartUploadCondition$,
  ]).pipe(
    map(
      ([uploadStarted, isPendingUpload, photos, creditsStartUploadCondition]) =>
        photos.length > 0 && !uploadStarted && (creditsStartUploadCondition || isPendingUpload),
    ),
  );
